import { useState } from 'react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const DEFAULT_RESIDENTIAL_STATES = [
  'AR', 'AZ', 'CT', 'CO', 'IA', 'KS', 'KY', 'LA', 'MA', 'ME',
  'MO', 'NC', 'NH', 'NJ', 'OH', 'OK', 'PA', 'RI', 'SC', 'TX',
  'VT', 'MD', 'FL'
];

const DEFAULT_COMMERCIAL_STATES = [
  'CT', 'IL', 'MD', 'MA', 'NJ', 'PA', 'RI', 'VA', 'DC', 'WV', 'ME', 'NH', 'VT'
];

interface StateSelectorProps {
  selectedStates: string[];
  onStateChange: (states: string[]) => void;
  label: string;
  defaultStates: string[];
  disabled?: boolean;
}

export const StateSelector: React.FC<StateSelectorProps> = ({
  selectedStates,
  onStateChange,
  label,
  defaultStates,
  disabled = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleStateToggle = (state: string) => {
    if (disabled) return;
    
    const newStates = selectedStates.includes(state)
      ? selectedStates.filter(s => s !== state)
      : [...selectedStates, state];
    onStateChange(newStates);
  };

  const handleSelectAll = () => {
    if (disabled) return;
    onStateChange(US_STATES);
  };

  const handleSelectDefault = () => {
    if (disabled) return;
    onStateChange(defaultStates);
  };

  const handleClearAll = () => {
    if (disabled) return;
    onStateChange([]);
  };

  const isDefault = JSON.stringify([...selectedStates].sort()) === JSON.stringify([...defaultStates].sort());

  return (
    <div className="space-y-3">
      {/* Header with title and summary */}
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium text-foreground">{label}</Label>
        <div className="flex items-center gap-3">
          {isDefault && (
            <Badge variant="secondary" className="text-xs font-medium">
              Default
            </Badge>
          )}
          <Badge variant="outline" className="text-xs">
            {selectedStates.length} of {US_STATES.length}
          </Badge>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            disabled={disabled}
            className="h-7 px-3 text-xs font-medium hover:bg-accent transition-colors"
          >
            {isExpanded ? 'Collapse' : 'Configure'}
          </Button>
        </div>
      </div>


      {/* Expanded state selection with animation */}
      {isExpanded && (
        <div className="animate-in slide-in-from-top-2 duration-200 border border-border rounded-lg bg-card/50 backdrop-blur-sm">
          {/* Quick action buttons */}
          <div className="p-4 border-b border-border">
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleSelectDefault}
                disabled={disabled}
                className="h-8 px-3 text-xs hover:bg-accent transition-colors"
              >
                Reset to Default ({defaultStates.length})
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
                disabled={disabled}
                className="h-8 px-3 text-xs hover:bg-accent transition-colors"
              >
                Select All States
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleClearAll}
                disabled={disabled}
                className="h-8 px-3 text-xs hover:bg-accent transition-colors"
              >
                Clear Selection
              </Button>
            </div>
          </div>

          {/* State grid - improved spacing */}
          <div className="p-4">
            <div className="grid grid-cols-5 gap-3">
              {US_STATES.map(state => (
                <div key={state} className="flex items-center space-x-2 group">
                  <Checkbox
                    id={`${label}-${state}`}
                    checked={selectedStates.includes(state)}
                    onCheckedChange={() => handleStateToggle(state)}
                    disabled={disabled}
                    className="h-4 w-4"
                  />
                  <Label
                    htmlFor={`${label}-${state}`}
                    className="text-sm font-mono cursor-pointer select-none group-hover:text-foreground transition-colors"
                  >
                    {state}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Empty state message */}
      {selectedStates.length === 0 && !isExpanded && (
        <div className="text-sm text-muted-foreground italic">
          No states selected
        </div>
      )}
    </div>
  );
};

interface UserStatesSelectorProps {
  residentialStates: string[];
  commercialStates: string[];
  onResidentialStatesChange: (states: string[]) => void;
  onCommercialStatesChange: (states: string[]) => void;
  disabled?: boolean;
}

export const UserStatesSelector: React.FC<UserStatesSelectorProps> = ({
  residentialStates,
  commercialStates,
  onResidentialStatesChange,
  onCommercialStatesChange,
  disabled = false
}) => {
  return (
    <div className="space-y-6">
      <StateSelector
        selectedStates={residentialStates}
        onStateChange={onResidentialStatesChange}
        label="Residential States"
        defaultStates={DEFAULT_RESIDENTIAL_STATES}
        disabled={disabled}
      />

      <StateSelector
        selectedStates={commercialStates}
        onStateChange={onCommercialStatesChange}
        label="Commercial States"
        defaultStates={DEFAULT_COMMERCIAL_STATES}
        disabled={disabled}
      />
    </div>
  );
};